import { isMissing, isNumericType } from "@/profitability/missing";
import type { RecordObject } from "@/profitability/records/objects";
import { recordId } from "@/profitability/records/objects";
import type { PartialBlank } from "@/profitability/records/record-view/missing-fields";
import type { ReportRow } from "@/profitability/types";
import { useFieldCitations } from "./query";
import { blankLines } from "./sourced";

/**
 * The record's figures that have a value but left blank lines of their
 * file out ("1 of 3 blank"): a gap the figure itself hides. Read from each
 * traced figure read from a file (the same queries "Show source" uses), so
 * only where the page offers it (`runId`); none until they load.
 */
export const useBlankLines = (
  businessId: string,
  runId: string | null | undefined,
  object: RecordObject,
  row: ReportRow,
): PartialBlank[] => {
  const { dataset } = object;
  const columns = runId
    ? dataset.columns.filter(
        (column) =>
          !!column.source &&
          !!dataset.traced?.includes(column.key) &&
          isNumericType(column.type) &&
          !isMissing(row[column.key]),
      )
    : [];
  const id = recordId(dataset, row);
  const results = useFieldCitations(
    businessId,
    runId ?? "",
    columns.map((column) => ({
      dataset: dataset.id,
      recordId: id,
      column: column.key,
    })),
  );
  return columns.flatMap((column, index) => {
    const lines = blankLines(results[index]?.data?.figure);
    return lines && lines.blank < lines.total ? [{ column, ...lines }] : [];
  });
};
